import * as React from "react";
import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

interface RepeatableListProps<T> {
  items: T[];
  onChange: (items: T[]) => void;
  /** Factory for a new blank row. */
  newItem: () => T;
  /** Render one row's fields; `update` merges a patch into that row. */
  renderRow: (
    item: T,
    update: (patch: Partial<T>) => void,
    index: number
  ) => React.ReactNode;
  addLabel: string;
  emptyLabel?: string;
  disabled?: boolean;
}

/**
 * Generic add/remove-row editor for arrays (workflows, custom rules,
 * blocks, fields, select options). Each row gets a Trash button; an
 * "Add" button appends a blank row. Immutable updates via onChange.
 */
export function RepeatableList<T>({
  items,
  onChange,
  newItem,
  renderRow,
  addLabel,
  emptyLabel,
  disabled,
}: RepeatableListProps<T>) {
  const { t } = useTranslation("common");
  const update = (index: number, patch: Partial<T>) => {
    onChange(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };
  const remove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {items.length === 0 && emptyLabel && (
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
      )}
      {items.map((item, index) => (
        <div
          key={index}
          className="flex items-start gap-2 rounded-md border border-border p-3"
        >
          <div className="min-w-0 flex-1">
            {renderRow(item, (patch) => update(index, patch), index)}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 text-muted-foreground hover:text-destructive"
            onClick={() => remove(index)}
            disabled={disabled}
            aria-label={t("fields.repeatable.remove")}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChange([...items, newItem()])}
        disabled={disabled}
      >
        <Plus className="mr-2 h-4 w-4" /> {addLabel}
      </Button>
    </div>
  );
}
